export interface WeatherData {
  temp: number;
  iconKey: string;
  description: string;
  hourly: { time: string; temp: number; code: number }[];
}

const WEATHER_CODE_MAP: Record<number, { iconKey: string; description: string }> = {
  0: { iconKey: 'sun', description: 'Clear sky' },
  1: { iconKey: 'partly', description: 'Mainly clear' },
  2: { iconKey: 'partly', description: 'Partly cloudy' },
  3: { iconKey: 'cloud', description: 'Overcast' },
  45: { iconKey: 'fog', description: 'Fog' },
  48: { iconKey: 'fog', description: 'Rime fog' },
  51: { iconKey: 'rain', description: 'Light drizzle' },
  53: { iconKey: 'rain', description: 'Drizzle' },
  55: { iconKey: 'rain', description: 'Dense drizzle' },
  61: { iconKey: 'rain', description: 'Light rain' },
  63: { iconKey: 'rain', description: 'Rain' },
  65: { iconKey: 'rain', description: 'Heavy rain' },
  66: { iconKey: 'rain', description: 'Freezing rain' },
  67: { iconKey: 'rain', description: 'Freezing rain' },
  71: { iconKey: 'snow', description: 'Light snow' },
  73: { iconKey: 'snow', description: 'Snow' },
  75: { iconKey: 'snow', description: 'Heavy snow' },
  77: { iconKey: 'snow', description: 'Snow grains' },
  80: { iconKey: 'rain', description: 'Rain showers' },
  81: { iconKey: 'rain', description: 'Rain showers' },
  82: { iconKey: 'rain', description: 'Violent showers' },
  85: { iconKey: 'snow', description: 'Snow showers' },
  86: { iconKey: 'snow', description: 'Snow showers' },
  95: { iconKey: 'storm', description: 'Thunderstorm' },
  96: { iconKey: 'storm', description: 'Thunderstorm' },
  99: { iconKey: 'storm', description: 'Thunderstorm' },
};

const cache = new Map<string, { data: WeatherData; timestamp: number }>();
const CACHE_TTL = 10 * 60 * 1000;

export async function getWeather(lat: number, lng: number): Promise<WeatherData | null> {
  const cacheKey = `${lat.toFixed(2)},${lng.toFixed(2)}`;
  const cached = cache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }

  try {
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,weather_code&hourly=temperature_2m,weather_code&forecast_days=1&timezone=auto`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const json = await res.json();

    const code = json.current?.weather_code ?? 0;
    const mapped = WEATHER_CODE_MAP[code] || WEATHER_CODE_MAP[0];
    const temp = Math.round(json.current?.temperature_2m ?? 0);

    const hourly = (json.hourly?.time || []).map((time: string, i: number) => ({
      time,
      temp: Math.round(json.hourly?.temperature_2m?.[i] ?? 0),
      code: json.hourly?.weather_code?.[i] ?? 0,
    }));

    const data: WeatherData = {
      temp,
      iconKey: mapped.iconKey,
      description: mapped.description,
      hourly,
    };

    cache.set(cacheKey, { data, timestamp: Date.now() });
    return data;
  } catch {
    return null;
  }
}
